/**
 * RoomCard — one row in the Sell Hub rooms editor.
 *
 * Renders a single room with type / level / L × W / flooring / features
 * inputs. Responsible for:
 *  - dim validation chrome (partial vs error per design §1.6)
 *  - inline delete-confirm strip on mobile (no modal)
 *  - reorder buttons (mobile up/down + desktop drag handle)
 *  - live "Area: N sf" readout
 *
 * The card does not own its position in the list — the parent (`RoomsEditor`)
 * owns the array order and passes `index` + `total` so we can compute "Room
 * {n}" labels and disable reorder buttons at endpoints.
 *
 * Props:
 *  - room: { id?, room_type, level, length_ft, width_ft, flooring, features }
 *  - index: number — 0-based position
 *  - total: number — total room count (for "Room {n} of {total}" SR text)
 *  - showErrors: boolean — show error chrome on out-of-range dims
 *  - onChange: (patch) => void
 *  - onDelete: () => void
 *  - onMoveUp: () => void  (no-op when index === 0)
 *  - onMoveDown: () => void  (no-op when index === total - 1)
 */

import React, { useCallback, useState } from 'react';
import { ROOM_TYPES, ROOM_LEVELS, ROOM_FLOORINGS } from './roomConstants';
import './RoomCard.css';

const DIM_MAX = 99.9;
const DIM_MIN = 0.1;
const FEATURES_MAX = 120;

const splitCase = (s) => s.replace(/([a-z])([A-Z])/g, '$1 $2');

const parseDim = (raw) => {
  if (raw === '' || raw == null) return null;
  const n = parseFloat(raw);
  if (Number.isNaN(n)) return null;
  return n;
};

const dimIsValid = (n) => n != null && n > 0 && n <= DIM_MAX;

const dimErrorMessage = (axis) =>
  `${axis === 'L' ? 'Length' : 'Width'} must be between ${DIM_MIN} and ${DIM_MAX} ft.`;

export default function RoomCard({
  room,
  index,
  total,
  showErrors = false,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleField = useCallback(
    (key) => (e) => onChange({ [key]: e.target.value }),
    [onChange],
  );

  const handleDim = useCallback(
    (key) => (e) => {
      // Allow free typing while editing (e.g. partial "1." en route to "1.5").
      // We don't strip the value — server-side and publish-time validators
      // enforce the bounds; the partial-row helper text covers in-progress
      // states.
      onChange({ [key]: e.target.value });
    },
    [onChange],
  );

  const length = parseDim(room.length_ft);
  const width = parseDim(room.width_ft);
  const lengthValid = dimIsValid(length);
  const widthValid = dimIsValid(width);
  // A row is "partial" if a value is missing; "error" if present-but-invalid.
  const lengthMissing = length == null && room.length_ft !== '' ? false : length == null;
  const widthMissing = width == null && room.width_ft !== '' ? false : width == null;
  const lengthError = length != null && !lengthValid;
  const widthError = width != null && !widthValid;

  const area = lengthValid && widthValid ? Math.round(length * width) : null;

  const roomNum = index + 1;

  // Mobile: tapping delete shows an inline confirm strip *inside the card*.
  // Desktop: same handler, no UX difference — the CSS just hides the trash
  // icon and shows a text-button "Delete room" instead.
  const handleDeleteClick = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
    } else {
      onDelete();
    }
  };

  const handleCancelDelete = () => setConfirmingDelete(false);

  return (
    <li
      className="room-card"
      aria-label={`Room ${roomNum} of ${total}`}
      data-testid="room-card"
    >
      <header className="room-card__head">
        <span
          className="room-card__handle"
          role="button"
          aria-label={`Reorder room ${roomNum}, currently position ${roomNum} of ${total}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              onMoveUp();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              onMoveDown();
            }
          }}
          data-testid="room-card-handle"
        >
          ⋮⋮
        </span>
        <span className="room-card__title">Room {roomNum}</span>
        <div className="room-card__head-actions">
          <button
            type="button"
            className="room-card__move"
            aria-label={`Move room ${roomNum} up`}
            disabled={index === 0}
            onClick={onMoveUp}
            data-testid="room-card-up"
          >
            <span aria-hidden="true">↑</span>
          </button>
          <button
            type="button"
            className="room-card__move"
            aria-label={`Move room ${roomNum} down`}
            disabled={index === total - 1}
            onClick={onMoveDown}
            data-testid="room-card-down"
          >
            <span aria-hidden="true">↓</span>
          </button>
          <button
            type="button"
            className="room-card__delete"
            aria-label={`Delete room ${roomNum}`}
            onClick={handleDeleteClick}
            data-testid="room-card-delete"
          >
            <span aria-hidden="true">🗑</span>
            <span className="room-card__delete-text">Delete room</span>
          </button>
        </div>
      </header>

      {confirmingDelete && (
        <div
          className="room-card__confirm"
          role="alertdialog"
          aria-label={`Confirm delete room ${roomNum}`}
          data-testid="room-card-confirm"
        >
          <span>Delete this room?</span>
          <div className="room-card__confirm-actions">
            <button
              type="button"
              className="room-card__confirm-cancel"
              onClick={handleCancelDelete}
              data-testid="room-card-confirm-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="room-card__confirm-delete"
              onClick={onDelete}
              data-testid="room-card-confirm-delete"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      <div className="room-card__body">
        <div className="room-card__field">
          <label
            className="listing-form__label"
            htmlFor={`room-type-${index}`}
          >
            Room type *
          </label>
          <select
            id={`room-type-${index}`}
            className="listing-form__input"
            value={room.room_type || ''}
            onChange={handleField('room_type')}
            data-testid="room-card-type"
          >
            <option value="">Select…</option>
            {ROOM_TYPES.map((t) => (
              <option key={t} value={t}>{splitCase(t)}</option>
            ))}
          </select>
        </div>

        <div className="room-card__field">
          <label
            className="listing-form__label"
            htmlFor={`room-level-${index}`}
          >
            Level *
          </label>
          <select
            id={`room-level-${index}`}
            className="listing-form__input"
            value={room.level || ''}
            onChange={handleField('level')}
            data-testid="room-card-level"
          >
            <option value="">Select…</option>
            {ROOM_LEVELS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        <div className="room-card__dims">
          <span className="listing-form__label room-card__dims-label">
            Dimensions (ft) *
          </span>
          <div className="room-card__dims-row">
            <div
              className={`room-card__dim ${
                lengthError && showErrors ? 'room-card__dim--error' : ''
              } ${lengthMissing ? 'room-card__dim--missing' : ''}`}
            >
              <span className="room-card__dim-prefix" aria-hidden="true">L</span>
              <label
                htmlFor={`room-length-${index}`}
                className="listing-form__visually-hidden"
              >
                Length in feet, room {roomNum}
              </label>
              <input
                id={`room-length-${index}`}
                className="listing-form__input room-card__dim-input"
                type="text"
                inputMode="decimal"
                pattern="\d*\.?\d?"
                value={room.length_ft ?? ''}
                onChange={handleDim('length_ft')}
                placeholder="12.0"
                aria-invalid={lengthError && showErrors ? 'true' : 'false'}
                aria-describedby={lengthError && showErrors ? `room-length-err-${index}` : undefined}
                data-testid="room-card-length"
              />
            </div>
            <span className="room-card__dim-x" aria-hidden="true">×</span>
            <div
              className={`room-card__dim ${
                widthError && showErrors ? 'room-card__dim--error' : ''
              } ${widthMissing ? 'room-card__dim--missing' : ''}`}
            >
              <span className="room-card__dim-prefix" aria-hidden="true">W</span>
              <label
                htmlFor={`room-width-${index}`}
                className="listing-form__visually-hidden"
              >
                Width in feet, room {roomNum}
              </label>
              <input
                id={`room-width-${index}`}
                className="listing-form__input room-card__dim-input"
                type="text"
                inputMode="decimal"
                pattern="\d*\.?\d?"
                value={room.width_ft ?? ''}
                onChange={handleDim('width_ft')}
                placeholder="14.5"
                aria-invalid={widthError && showErrors ? 'true' : 'false'}
                aria-describedby={widthError && showErrors ? `room-width-err-${index}` : undefined}
                data-testid="room-card-width"
              />
            </div>
            {area != null && (
              <span className="room-card__area" data-testid="room-card-area">
                Area: {area} sf
              </span>
            )}
          </div>
          {(lengthMissing || widthMissing) && !lengthError && !widthError && (
            <p className="room-card__dim-help">
              Add L and W to include this room in your published listing.
            </p>
          )}
          {lengthError && showErrors && (
            <p
              className="room-card__dim-error"
              id={`room-length-err-${index}`}
              role="alert"
            >
              {dimErrorMessage('L')}
            </p>
          )}
          {widthError && showErrors && (
            <p
              className="room-card__dim-error"
              id={`room-width-err-${index}`}
              role="alert"
            >
              {dimErrorMessage('W')}
            </p>
          )}
        </div>

        <div className="room-card__field">
          <label
            className="listing-form__label"
            htmlFor={`room-flooring-${index}`}
          >
            Flooring
          </label>
          <select
            id={`room-flooring-${index}`}
            className="listing-form__input"
            value={room.flooring || ''}
            onChange={handleField('flooring')}
            data-testid="room-card-flooring"
          >
            <option value="">—</option>
            {ROOM_FLOORINGS.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>

        <div className="room-card__field room-card__field--features">
          <label
            className="listing-form__label"
            htmlFor={`room-features-${index}`}
          >
            Features (optional)
          </label>
          <input
            id={`room-features-${index}`}
            className="listing-form__input"
            type="text"
            value={room.features ?? ''}
            onChange={handleField('features')}
            maxLength={FEATURES_MAX}
            placeholder="Walk-in closet, ensuite"
            data-testid="room-card-features"
          />
          <span className="listing-form__char-count">
            {(room.features || '').length} / {FEATURES_MAX}
          </span>
        </div>
      </div>
    </li>
  );
}
